import { Component, OnInit, Output, EventEmitter, Input, OnDestroy } from '@angular/core';
import { CompanyInfoFilters } from './company-info-filters.model';
import { PayGroup } from '../../../shared/models/pay-group.model';
import { PayGroupService } from '../../../services/paygroup.service';
import { map, takeUntil, concatMap, toArray } from 'rxjs/operators';
import { CompanyInfoFiltersService } from './company-info-filters.service';
import { sortBy } from 'lodash';
import { BaseObserver } from '../../../shared/baseobserver';
import { Subscription } from 'rxjs';
import { CompanyBasicInfo } from '../../../services/company.model';
import { CompanyService } from '../../../services/company.service';
import { SettingsService } from '../../../shared/services/settings/settings.service';

@Component({
  selector: 'ssg-company-info-filters',
  templateUrl: './company-info-filters.component.html',
  styleUrls: ['./company-info-filters.component.less']
})
export class CompanyInfoFiltersComponent extends BaseObserver implements OnInit, OnDestroy {
  @Output() toggle: EventEmitter<boolean>;
  private _lastFilters: CompanyInfoFilters;
  private _subscriptions: Subscription[];
  editFilters: CompanyInfoFilters;
  allPayGroups: string[] = [];
  companiesDataSource: CompanyBasicInfo[] = [];
  companyCodesDataSource: CompanyBasicInfo[] = [];
  companyFeinsDataSource: string[] = [];

  isEditing = false;
  isInvalid = false;

  @Input() isWorking: boolean;

  constructor(private readonly _payGroupService: PayGroupService,
    private readonly _companyService: CompanyService,
    private readonly _filtersService: CompanyInfoFiltersService,
    private readonly _settings: SettingsService) {

    super();
    this.toggle = new EventEmitter<boolean>();
  }

  ngOnInit() {
    this.getAllPayGroups();
    this.getAllCompaniesDataSources();
    this.getFeinDataSource();

    this._lastFilters = null;
    this._subscriptions = [];

    this._subscriptions.push(this._filtersService.getFilters().subscribe(filters => this._handleFiltersUpdated(filters)));
  }

  ngOnDestroy() {
    if (this._subscriptions) {
      for (const subscription of this._subscriptions) {
        subscription.unsubscribe();
      }

      this._subscriptions = null;
    }
  }

  applyEdits() {
    if (this.isWorking) {
      return;
    }

    this._filtersService.updateFilters(this.editFilters);
  }

  clearEditFilters() {
    this.editFilters.reset();
    this.applyEdits();
  }

  toggleFilters(value: boolean) {
    this.toggle.emit(value);
  }

  getCode(payGroup: PayGroup) {
    return payGroup.code.trim();
  }

  getAllPayGroups(): void {
    this._payGroupService.getPayGroups(false)
      .pipe(
        map(groups => sortBy(groups, ['code', 'name'])),
        takeUntil(this.destroy$)
      )
      .subscribe(result => this.allPayGroups = result.map(p => p.code));
  }

  private getAllCompaniesDataSources(): void {
    this._companyService.getCompanyBasicInfos(this._settings.tenantId, 1, 10)
      .pipe(
        map(companies => sortBy(companies, ['legalName'])),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
          this.companiesDataSource = result;
          this.companyCodesDataSource = result;
        });
  }

  private getFeinDataSource(): void {
    this._companyService.getCompanyBasicInfos(this._settings.tenantId, 1, 10, null, null, null, true)
      .pipe(
        map(companies => sortBy(companies, ['fein'])),
        takeUntil(this.destroy$)
      )
      .subscribe(result => this.companyFeinsDataSource = Array.from(new Set(result.map(c => c.fein))));
  }

  private _handleFiltersUpdated(filters: CompanyInfoFilters) {
    if (this._lastFilters !== null &&
      CompanyInfoFilters.areEqual(this._lastFilters, filters)) {

      return;
    }

    this.editFilters = filters.clone();
    this._lastFilters = filters;
  }

  searchCompaniesByLegalName(e) {
    const text = e.term;
    this._companyService.getCompanyBasicInfos(this._settings.tenantId, 1, 10, text)
      .pipe(
        map(companies => sortBy(companies, ['legalName'])),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
          this.companiesDataSource = result;
        });
  }

  searchCompaniesByCompanyCode(e) {
    const text = e.term;
    this._companyService.getCompanyBasicInfos(this._settings.tenantId, 1, 10, null, null, text)
      .pipe(
        map(companies => sortBy(companies, ['companyCode'])),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
          this.companyCodesDataSource = result;
        });
  }

  searchCompaniesByFein(e) {
    const text = e.term;
    this._companyService.getCompanyBasicInfos(this._settings.tenantId, 1, 10, null, text, null, true)
      .pipe(
        map(companies => sortBy(companies, ['fein'])),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
          this.companyFeinsDataSource = Array.from(new Set(result.map(c => c.fein)));
        });
  }
}
